import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Menu, X, Star, Users, Clock, Phone, Mail, Calendar, MapPin, Building, CreditCard, TestTube2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ScrollingLogo from '@/components/ScrollingLogo';

const WHATSAPP_MESSAGE = "Hi, I'm interested in booking a corporate trainer from My Corporate Hub.";

const trainersData = [
  {
    id: 1,
    category: 'Soft Skills',
    title: 'Soft Skills Trainer',
    description: 'Expert trainers specializing in communication, leadership, teamwork, and interpersonal skills development for corporate professionals.',
    skills: ['Communication Skills', 'Leadership Development', 'Team Building', 'Conflict Resolution', 'Emotional Intelligence'],
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=300&fit=crop',
    rating: 4.9,
    sessions: '500+',
    experience: '10+ years',
    pricePerSession: 15000,
    pricePerDay: 75000
  },
  {
    id: 2,
    category: 'Wellness',
    title: 'Corporate Yoga Instructor',
    description: 'Certified yoga instructors bringing mindfulness and physical wellness to the workplace through customized yoga sessions.',
    skills: ['Desk Yoga', 'Stress Relief', 'Meditation', 'Breathing Techniques', 'Posture Correction'],
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
    rating: 4.8,
    sessions: '300+',
    experience: '8+ years',
    pricePerSession: 8000,
    pricePerDay: 40000
  },
  {
    id: 3,
    category: 'Health',
    title: 'Corporate Nutritionist',
    description: 'Professional nutritionists helping employees make healthier food choices and improve overall wellness through personalized guidance.',
    skills: ['Diet Planning', 'Weight Management', 'Healthy Eating Habits', 'Nutrition Workshops', 'Meal Planning'],
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop',
    rating: 4.7,
    sessions: '200+',
    experience: '7+ years',
    pricePerSession: 10000,
    pricePerDay: 50000
  },
  {
    id: 4,
    category: 'Motivation',
    title: 'Motivational Speaker',
    description: 'Inspiring speakers who energize teams, boost morale, and drive performance through powerful keynotes and workshops.',
    skills: ['Keynote Speaking', 'Goal Setting', 'Performance Motivation', 'Change Management', 'Resilience Building'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400&h=300&fit=crop',
    rating: 4.9,
    sessions: '400+',
    experience: '12+ years',
    pricePerSession: 50000,
    pricePerDay: 150000
  },
  {
    id: 5,
    category: 'Technical',
    title: 'Technical Skills Trainer',
    description: 'Industry experts providing hands-on training in latest technologies, tools, and technical methodologies.',
    skills: ['Software Training', 'Data Analytics', 'Cloud Computing', 'Cybersecurity', 'AI/ML Fundamentals'],
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=300&fit=crop',
    rating: 4.8,
    sessions: '350+',
    experience: '9+ years',
    pricePerSession: 20000,
    pricePerDay: 100000
  },
  {
    id: 6,
    category: 'Sales',
    title: 'Sales & Negotiation Coach',
    description: 'Experienced sales trainers helping teams master the art of selling, negotiation, and client relationship management.',
    skills: ['Sales Techniques', 'Negotiation Skills', 'Client Management', 'Presentation Skills', 'Closing Strategies'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
    rating: 4.8,
    sessions: '450+',
    experience: '11+ years',
    pricePerSession: 25000,
    pricePerDay: 120000
  },
  {
    id: 7,
    category: 'Wellness',
    title: 'Mental Health Counselor',
    description: 'Licensed counselors providing mental health support, stress management, and work-life balance guidance.',
    skills: ['Stress Management', 'Anxiety Support', 'Work-Life Balance', 'Burnout Prevention', 'Mindfulness'],
    image: 'https://images.unsplash.com/photo-1573497019236-17f8177b81e8?w=400&h=300&fit=crop',
    rating: 4.9,
    sessions: '250+',
    experience: '8+ years',
    pricePerSession: 12000,
    pricePerDay: 60000
  },
  {
    id: 8,
    category: 'Leadership',
    title: 'Executive Coach',
    description: 'Senior executive coaches specializing in C-suite development, strategic thinking, and organizational leadership.',
    skills: ['Executive Presence', 'Strategic Planning', 'Decision Making', 'Board Presentations', 'Crisis Leadership'],
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop',
    rating: 4.9,
    sessions: '180+',
    experience: '15+ years',
    pricePerSession: 75000,
    pricePerDay: 300000
  },
  {
    id: 9,
    category: 'Communication',
    title: 'Public Speaking Coach',
    description: 'Expert coaches helping professionals overcome stage fear and deliver impactful presentations with confidence.',
    skills: ['Stage Presence', 'Voice Modulation', 'Body Language', 'Storytelling', 'Audience Engagement'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400&h=300&fit=crop',
    rating: 4.8,
    sessions: '280+',
    experience: '10+ years',
    pricePerSession: 18000,
    pricePerDay: 90000
  },
  {
    id: 10,
    category: 'Diversity',
    title: 'DEI Trainer',
    description: 'Specialists in Diversity, Equity & Inclusion training to create inclusive and respectful workplace cultures.',
    skills: ['Unconscious Bias', 'Inclusive Leadership', 'Cultural Sensitivity', 'Harassment Prevention', 'Allyship'],
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=300&fit=crop',
    rating: 4.7,
    sessions: '150+',
    experience: '6+ years',
    pricePerSession: 20000,
    pricePerDay: 100000
  },
  {
    id: 11,
    category: 'Fitness',
    title: 'Corporate Fitness Trainer',
    description: 'Certified fitness professionals conducting group workouts, fitness challenges, and wellness programs.',
    skills: ['Group Fitness', 'HIIT Training', 'Strength Training', 'Flexibility', 'Corporate Challenges'],
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop',
    rating: 4.8,
    sessions: '320+',
    experience: '7+ years',
    pricePerSession: 8000,
    pricePerDay: 40000
  },
  {
    id: 12,
    category: 'Creativity',
    title: 'Innovation & Design Thinking Coach',
    description: 'Creative facilitators teaching design thinking, innovation methodologies, and creative problem-solving.',
    skills: ['Design Thinking', 'Brainstorming', 'Prototyping', 'Innovation Sprints', 'Creative Problem Solving'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
    rating: 4.7,
    sessions: '200+',
    experience: '8+ years',
    pricePerSession: 22000,
    pricePerDay: 110000
  }
];

const categories = ['All', 'Soft Skills', 'Wellness', 'Health', 'Motivation', 'Technical', 'Sales', 'Leadership', 'Communication', 'Diversity', 'Fitness', 'Creativity'];

export default function TrainersPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [eventDate, setEventDate] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [customAmount, setCustomAmount] = useState(1);
  const [bookingData, setBookingData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    event_type: '',
    attendees: '',
    duration: 'half_day',
    venue_address: '',
    city: '',
    special_requirements: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredTrainers = selectedCategory === 'All' 
    ? trainersData 
    : trainersData.filter(t => t.category === selectedCategory);

  const handleBookTrainer = (trainer) => {
    setSelectedTrainer(trainer);
    if (user) {
      setBookingData(prev => ({
        ...prev,
        contact_name: user.name || '',
        contact_email: user.email || '',
        company_name: user.company || ''
      }));
    }
    setShowBookingDialog(true);
  };

  const calculatePrice = () => {
    if (!selectedTrainer) return 0;
    if (bookingData.duration === 'session') return selectedTrainer.pricePerSession;
    if (bookingData.duration === 'half_day') return selectedTrainer.pricePerDay * 0.6;
    if (bookingData.duration === 'full_day') return selectedTrainer.pricePerDay;
    if (bookingData.duration === 'multi_day') return selectedTrainer.pricePerDay * 0.9; // 10% discount for multi-day
    return selectedTrainer.pricePerSession;
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to book a trainer');
      return;
    }
    
    if (!eventDate) {
      toast.error('Please select an event date');
      return;
    }

    if (!bookingData.event_type || !bookingData.attendees || !bookingData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    setBookingLoading(true);
    
    try {
      // Create trainer booking request
      const bookingPayload = {
        trainer_id: selectedTrainer.id,
        trainer_type: selectedTrainer.title,
        trainer_category: selectedTrainer.category,
        ...bookingData,
        event_date: eventDate.toISOString(),
        estimated_price: calculatePrice()
      };
      
      // Create booking in database
      const bookingResponse = await api.createTrainerBooking(bookingPayload);
      const bookingId = bookingResponse.data.id;
      
      // Create Razorpay payment order - use custom amount if test mode enabled
      const paymentAmount = testMode && customAmount > 0 ? customAmount : null;
      if (testMode && customAmount > 0) {
        toast.info(`Test Mode: Processing payment for ₹${customAmount} instead of ₹${calculatePrice().toLocaleString()}`);
      }
      
      const paymentResponse = await api.createTrainerBookingPayment(bookingId, paymentAmount);
      const { order_id, amount, currency, key_id } = paymentResponse.data;
      
      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'My Corporate Hub',
        description: `Trainer Booking: ${selectedTrainer.title}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await api.verifyTrainerBookingPayment(bookingId, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            toast.success('Payment successful! Your trainer booking is confirmed.');
            setShowBookingDialog(false);
            setSelectedTrainer(null);
            setEventDate(null);
            setBookingData({
              company_name: '',
              contact_name: '',
              contact_email: '',
              contact_phone: '',
              event_type: '',
              attendees: '',
              duration: 'half_day',
              venue_address: '',
              city: '',
              special_requirements: ''
            });
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: bookingData.contact_name,
          email: bookingData.contact_email,
          contact: bookingData.contact_phone
        },
        notes: {
          booking_id: bookingId,
          trainer_type: selectedTrainer.title
        },
        theme: {
          color: '#0A1628'
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled. Your booking is saved and you can pay later.');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-gold font-medium text-sm uppercase tracking-wide">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-gold/20 text-gold rounded-full text-sm font-semibold mb-4 uppercase tracking-wide border border-gold/30">
              Corporate Trainers
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Expert Corporate Trainers
            </h1>
            <p className="text-white/90 text-lg max-w-2xl mx-auto">
              Connect with India's top corporate trainers for skill development, wellness programs, and professional growth
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-navy">500+</div>
              <div className="text-slate-600 text-sm">Verified Trainers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-navy">12+</div>
              <div className="text-slate-600 text-sm">Categories</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-navy">10,000+</div>
              <div className="text-slate-600 text-sm">Sessions Conducted</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-navy">4.8★</div>
              <div className="text-slate-600 text-sm">Average Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-navy text-white'
                  : 'bg-white text-navy border border-gray-200 hover:border-gold hover:text-gold'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Trainers Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTrainers.map(trainer => (
            <Card key={trainer.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={trainer.image} 
                  alt={trainer.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-gold text-navy text-xs font-bold rounded-full">
                    {trainer.category}
                  </span>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 px-2 py-1 rounded-full">
                  <Star className="w-4 h-4 text-gold fill-gold" />
                  <span className="text-sm font-semibold text-navy">{trainer.rating}</span>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-bold text-navy mb-2">{trainer.title}</h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{trainer.description}</p>
                
                <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{trainer.sessions} sessions</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{trainer.experience}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {trainer.skills.slice(0, 3).map((skill, idx) => (
                    <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {skill}
                    </span>
                  ))}
                  {trainer.skills.length > 3 && (
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      +{trainer.skills.length - 3} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-xs text-slate-500">Starting from</span>
                    <p className="text-lg font-bold text-navy">₹{trainer.pricePerSession.toLocaleString()}<span className="text-xs font-normal text-slate-500">/session</span></p>
                  </div>
                </div>

                <Button 
                  className="w-full bg-navy hover:bg-navy-light text-white"
                  onClick={() => handleBookTrainer(trainer)}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Trainer
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">
              Book {selectedTrainer?.title}
            </DialogTitle>
            <p className="text-slate-600 text-sm">Fill in your event details to book this trainer</p>
          </DialogHeader>

          <form onSubmit={handleSubmitBooking} className="space-y-6 mt-4">
            {/* Selected Trainer Info */}
            {selectedTrainer && (
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <img src={selectedTrainer.image} alt={selectedTrainer.title} className="w-16 h-16 rounded-lg object-cover" />
                <div>
                  <h4 className="font-semibold text-navy">{selectedTrainer.title}</h4>
                  <p className="text-sm text-slate-600">{selectedTrainer.category} • {selectedTrainer.rating}★</p>
                </div>
              </div>
            )}

            {/* Company & Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input 
                  id="company_name"
                  value={bookingData.company_name}
                  onChange={(e) => setBookingData({...bookingData, company_name: e.target.value})}
                  placeholder="Your company name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact_name">Contact Person *</Label>
                <Input 
                  id="contact_name"
                  value={bookingData.contact_name}
                  onChange={(e) => setBookingData({...bookingData, contact_name: e.target.value})}
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Email *</Label>
                <Input 
                  id="contact_email"
                  type="email"
                  value={bookingData.contact_email}
                  onChange={(e) => setBookingData({...bookingData, contact_email: e.target.value})}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Phone *</Label>
                <Input 
                  id="contact_phone"
                  value={bookingData.contact_phone}
                  onChange={(e) => setBookingData({...bookingData, contact_phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>
            </div>

            {/* Event Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_type">Event Type *</Label>
                <Select value={bookingData.event_type} onValueChange={(value) => setBookingData({...bookingData, event_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="training">Training Session</SelectItem>
                    <SelectItem value="seminar">Seminar</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="team_building">Team Building</SelectItem>
                    <SelectItem value="offsite">Corporate Offsite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="attendees">Expected Attendees *</Label>
                <Select value={bookingData.attendees} onValueChange={(value) => setBookingData({...bookingData, attendees: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10-25">10-25 people</SelectItem>
                    <SelectItem value="26-50">26-50 people</SelectItem>
                    <SelectItem value="51-100">51-100 people</SelectItem>
                    <SelectItem value="101-200">101-200 people</SelectItem>
                    <SelectItem value="200+">200+ people</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event Date *</Label>
                <DatePicker
                  selected={eventDate}
                  onChange={(date) => setEventDate(date)}
                  minDate={new Date()}
                  placeholderText="Select event date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration *</Label>
                <Select value={bookingData.duration} onValueChange={(value) => setBookingData({...bookingData, duration: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="session">Single Session (2-3 hrs)</SelectItem>
                    <SelectItem value="half_day">Half Day (4-5 hrs)</SelectItem>
                    <SelectItem value="full_day">Full Day (8 hrs)</SelectItem>
                    <SelectItem value="multi_day">Multi-Day Program</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Venue Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Select value={bookingData.city} onValueChange={(value) => setBookingData({...bookingData, city: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mumbai">Mumbai</SelectItem>
                    <SelectItem value="Delhi">Delhi NCR</SelectItem>
                    <SelectItem value="Bangalore">Bangalore</SelectItem>
                    <SelectItem value="Chennai">Chennai</SelectItem>
                    <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                    <SelectItem value="Pune">Pune</SelectItem>
                    <SelectItem value="Kolkata">Kolkata</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="venue_address">Venue Address</Label>
                <Input 
                  id="venue_address"
                  value={bookingData.venue_address}
                  onChange={(e) => setBookingData({...bookingData, venue_address: e.target.value})}
                  placeholder="Venue address (optional)"
                />
              </div>
            </div>

            {/* Special Requirements */}
            <div>
              <Label htmlFor="special_requirements">Special Requirements</Label>
              <Textarea 
                id="special_requirements"
                value={bookingData.special_requirements}
                onChange={(e) => setBookingData({...bookingData, special_requirements: e.target.value})}
                placeholder="Any specific topics, objectives, or requirements..."
                rows={3}
              />
            </div>

            {/* Test Mode for Payment Testing */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="trainerTestMode" 
                  checked={testMode}
                  onCheckedChange={(checked) => setTestMode(checked)}
                />
                <Label htmlFor="trainerTestMode" className="flex items-center gap-2 cursor-pointer">
                  <TestTube2 className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-medium">Test Mode (₹1 Payment)</span>
                </Label>
              </div>
              {testMode && (
                <div className="space-y-2">
                  <Label htmlFor="trainerCustomAmount" className="text-amber-700 text-sm">
                    Enter custom payment amount (₹)
                  </Label>
                  <Input 
                    id="trainerCustomAmount"
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 1)}
                    className="bg-white border-amber-300 focus:border-amber-500"
                    placeholder="Enter amount in rupees (default: 1)"
                  />
                  <p className="text-xs text-amber-600">
                    ⚠️ Test payment will be processed for ₹{customAmount} instead of actual booking cost (₹{calculatePrice().toLocaleString()})
                  </p>
                </div>
              )}
            </div>

            {/* Pricing Summary */}
            <div className="p-4 bg-gold/10 border border-gold/30 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-navy">Estimated Price:</span>
                <span className="text-2xl font-bold text-navy">₹{calculatePrice().toLocaleString()}</span>
              </div>
              <p className="text-xs text-slate-600 mt-1">Final price may vary based on specific requirements. Our team will confirm the exact quote.</p>
            </div>

            {/* Razorpay Verified Badge */}
            <div className="flex flex-col items-center gap-3 py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-slate-900 font-semibold">Secure Payments Powered By</span>
              <div className="flex items-center gap-4">
                <img 
                  src="https://cdn.razorpay.com/logo.svg" 
                  alt="Razorpay" 
                  className="h-5"
                />
                <div className="w-px h-6 bg-gray-300"></div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-4"
                />
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-6"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowBookingDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-gold hover:bg-gold-light text-navy font-bold"
                disabled={bookingLoading}
              >
                {bookingLoading ? 'Submitting...' : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Submit Booking Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer - Same as Landing Page */}
      <footer className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Newsletter Section */}
          <div className="bg-gradient-to-r from-gold/20 to-gold/10 border-2 border-gold/50 rounded-xl p-8 mb-12 shadow-lg">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-white mb-2">🔔 Stay Updated with Event Insights</h3>
              <p className="text-slate-200 mb-6">Get exclusive event planning tips, venue updates, and special offers</p>
              <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 bg-white border-gold/30 text-navy placeholder:text-slate-700 h-12"
                />
                <Button type="submit" className="bg-gold hover:bg-gold-light text-navy font-bold whitespace-nowrap px-8 h-12 shadow-md">
                  Subscribe Now
                </Button>
              </form>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                  alt="My Corporate Hub" 
                  className="h-12 w-auto"
                />
                <span className="font-heading text-2xl text-white font-semibold">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
              </div>
              <p className="text-slate-300">India's premier platform for corporate events, conferences, and luxury hospitality.</p>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-slate-300 hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/properties" className="text-slate-300 hover:text-white transition-colors">Properties</Link></li>
                <li><Link to="/gifts" className="text-slate-300 hover:text-white transition-colors">Corporate Gifting</Link></li>
                <li><Link to="/event-permissions" className="text-slate-300 hover:text-white transition-colors">Event Permits</Link></li>
                <li><Link to="/trainers" className="text-slate-300 hover:text-white transition-colors">Trainers</Link></li>
                <li><Link to="/careers" className="text-slate-300 hover:text-white transition-colors">Careers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-slate-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="text-slate-300 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund-policy" className="text-slate-300 hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="text-slate-300 hover:text-white transition-colors">Cancellation Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Contact Us</h4>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start">
                  <span className="mr-2">📞</span>
                  <span>+91 96648 70766</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✉️</span>
                  <span>info@mycorporatehub.com</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">💬</span>
                  <a href={`https://wa.me/919664870766?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                    WhatsApp: +91 96648 70766
                  </a>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📍</span>
                  <span>Corporate Office:<br/>L23, Express Zone, A Wing,<br/>Western Express Highway, Panch Bawadi,<br/>Malad East, Mumbai 400063,<br/>Maharashtra, India</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏢</span>
                  <span>GST: 27AAMCB6393G1Z9</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Payment Partner - Razorpay */}
          <div className="border-t border-navy-700 mt-8 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <span className="text-white text-sm font-medium">Secure Payments Powered By</span>
              <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-lg shadow-lg">
                <img 
                  src="https://cdn.razorpay.com/logo.svg" 
                  alt="Razorpay" 
                  className="h-6"
                />
                <div className="w-px h-8 bg-slate-300"></div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-5"
                />
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-8"
                />
                <div className="w-px h-8 bg-slate-300"></div>
                <img 
                  src="https://badges.razorpay.com/badge-light.png" 
                  alt="Razorpay Trusted Business" 
                  className="h-10"
                />
              </div>
            </div>
          </div>
          
          <div className="border-t border-navy-700 mt-6 pt-8 text-center">
            <p className="text-slate-300 text-sm">© 2025 My Corporate Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
